import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ptBR from '@i18n-content/pt-BR.json';
import ptPT from '@i18n-content/pt-PT.json';
import en from '@i18n-content/en.json';
import es from '@i18n-content/es.json';
import fr from '@i18n-content/fr.json';

/**
 * i18n setup — react-i18next.
 * 5 idiomas. JSONs em ../../i18n/{locale}.json (compartilhado com backend).
 *
 * Decisao: pre-carregar todos os bundles. Total ~110KB gz dos 5.
 * Lazy load por idioma adicionava race condition no LocaleGate (renderizava
 * com fallback pt-BR enquanto bundle do idioma alvo estava em flight).
 * Pra projeto pequeno (~700 chaves x 5), eager load e mais robusto.
 */

export const SUPPORTED_LOCALES = ['pt-BR', 'pt-PT', 'en', 'es', 'fr'] as const;
export type Locale = typeof SUPPORTED_LOCALES[number];

const envDefault = (import.meta.env.VITE_DEFAULT_LOCALE as string | undefined) ?? 'pt-BR';
export const DEFAULT_LOCALE: Locale =
  (SUPPORTED_LOCALES as readonly string[]).includes(envDefault) ? (envDefault as Locale) : 'pt-BR';

export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: false,
    interpolation: { escapeValue: false }, // React ja escapa
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0, // primeiro segmento da URL = locale
      lookupLocalStorage: 'preferred_locale',
      caches: ['localStorage'],
    },
    react: { useSuspense: false }, // sem suspense pq bundles ja vem inline
    resources: {
      'pt-BR': { translation: ptBR },
      'pt-PT': { translation: ptPT },
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
    },
  });

export default i18n;
